import axios from 'axios';
import { PRINT_IFRAME_CLEANUP_FALLBACK_MS } from '../constants/picking';

export async function printLabelFromUrl(pdfUrl: string): Promise<void> {
  const response = await axios.get(pdfUrl, { responseType: 'blob' });
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const blobUrl = window.URL.createObjectURL(blob);

  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.left = '-9999px';
  iframe.style.top = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.src = blobUrl;
  document.body.appendChild(iframe);

  return new Promise((resolve, reject) => {
    let cleanedUp = false;
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

    const removeIframe = () => {
      window.URL.revokeObjectURL(blobUrl);
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
    };

    const onWindowFocus = () => {
      // Main window regains focus when the native print dialog closes
      // (Print or Cancel). Defer slightly so a pending onafterprint
      // handler wins the race if both fire in the same tick.
      setTimeout(cleanup, 0);
    };

    const cleanup = () => {
      if (cleanedUp) return;
      cleanedUp = true;

      if (fallbackTimer !== null) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
      window.removeEventListener('focus', onWindowFocus);

      removeIframe();
      resolve();
    };

    iframe.onload = () => {
      try {
        const win = iframe.contentWindow;
        if (!win) {
          cleanedUp = true;
          removeIframe();
          reject(new Error('Print iframe has no contentWindow'));
          return;
        }

        // Primary cleanup path: fires on both Print and Cancel in
        // the Windows native dialog.
        win.onafterprint = cleanup;

        // Backup: focus returns to the main window when the dialog closes.
        window.addEventListener('focus', onWindowFocus);

        // Safety net against leaks if neither event fires.
        fallbackTimer = setTimeout(cleanup, PRINT_IFRAME_CLEANUP_FALLBACK_MS);

        // focus() before print() is required by some Chromium builds
        // on Windows to route the dialog correctly.
        iframe.focus();
        win.focus();
        win.print();
      } catch (error) {
        cleanedUp = true;
        window.removeEventListener('focus', onWindowFocus);
        if (fallbackTimer !== null) clearTimeout(fallbackTimer);
        removeIframe();
        reject(error);
      }
    };

    iframe.onerror = () => {
      cleanedUp = true;
      removeIframe();
      reject(new Error('Failed to load label PDF'));
    };
  });
}
